import exercisesData from '../data/exercises.json';

/**
 * Exercise compatibility constraints and groupings
 */
export const EXERCISE_CONSTRAINTS = {
  // Equipment location constraints
  location: {
    outdoor: ['outdoor', 'park', 'trail', 'beach', 'track'],
    indoor: ['gym', 'home', 'studio', 'indoor'],
    equipment: ['machine', 'barbell', 'dumbbell', 'kettlebell', 'cable', 'smith machine']
  },
  
  // Exercise type compatibility
  type: {
    cardio: ['cardio', 'hiit', 'running', 'cycling', 'rowing', 'elliptical'],
    strength: ['strength', 'powerlifting', 'bodybuilding'],
    flexibility: ['stretching', 'yoga', 'mobility'],
    plyometric: ['plyometrics', 'jumping', 'explosive']
  },
  
  // Equipment compatibility
  equipment: {
    bodyweight: ['body only', 'none'],
    gym: ['barbell', 'dumbbell', 'machine', 'cable', 'smith machine'],
    home: ['dumbbell', 'kettlebell', 'resistance band', 'body only'],
    outdoor: ['body only', 'none', 'resistance band']
  }
};

/**
 * Smart workout generation with practical constraints
 * @param {Object} criteria - Selection criteria
 * @param {Array} criteria.muscleGroups - Selected muscle groups
 * @param {string} criteria.category - Exercise category
 * @param {string} criteria.level - Difficulty level
 * @param {number} count - Number of exercises to generate
 * @returns {Array} Generated workout exercises
 */
export const generateSmartWorkout = (criteria, count = 5) => {
  const { muscleGroups = [], category, level, equipment } = criteria;
  
  console.log('Smart workout generation criteria:', criteria);
  
  // Step 1: Filter exercises by basic criteria
  let candidates = exercisesData.filter(exercise => {
    const matchesMuscle = muscleGroups.length === 0 || 
      exercise.primaryMuscles?.some(muscle => 
        muscleGroups.some(group => 
          getMuscleGroupAliases(group).includes(muscle.toLowerCase())
        )
      );
    
    const matchesCategory = !category || 
      exercise.category?.toLowerCase() === category.toLowerCase();
    
    const matchesLevel = !level || 
      exercise.level?.toLowerCase() === level.toLowerCase();
    
    return matchesMuscle && matchesCategory && matchesLevel;
  });
  
  // If no candidates found and we have a category filter, try without muscle group constraint
  if (candidates.length === 0 && category) {
    console.log('No muscle-specific exercises found, trying category-only filter...');
    candidates = exercisesData.filter(exercise => {
      const matchesCategory = exercise.category?.toLowerCase() === category.toLowerCase();
      const matchesLevel = !level || exercise.level?.toLowerCase() === level.toLowerCase();
      return matchesCategory && matchesLevel;
    });
    console.log('Category-only candidates found:', candidates.length);
  }
  
  // Don't force equipment restrictions - let the smart grouping handle it naturally
  
  console.log('Filtered candidates:', candidates.length);
  console.log('Sample candidates:', candidates.slice(0, 3).map(ex => ({ name: ex.name, category: ex.category, primaryMuscles: ex.primaryMuscles })));
  
  if (candidates.length === 0) {
    console.log('No candidates found. Debugging...');
    console.log('Available cardio exercises:', exercisesData.filter(ex => ex.category?.toLowerCase() === 'cardio').length);
    console.log('Available chest exercises:', exercisesData.filter(ex => ex.primaryMuscles?.includes('chest')).length);
    console.log('Available back exercises:', exercisesData.filter(ex => ex.primaryMuscles?.includes('lats') || ex.primaryMuscles?.includes('middle back')).length);
    return [];
  }
  
  // Step 2: Apply smart constraints
  const workout = [];
  const usedExercises = new Set();
  
  // Determine workout context (indoor/outdoor, equipment type)
  const context = determineWorkoutContext(candidates, criteria);
  
  // Step 3: Select exercises with smart grouping
  for (let i = 0; i < count && candidates.length > 0; i++) {
    const availableExercises = candidates.filter(ex => !usedExercises.has(ex.id));
    
    if (availableExercises.length === 0) break;
    
    // Apply context-aware filtering
    const contextFiltered = filterByContext(availableExercises, context, workout);
    
    // Select exercise with smart scoring
    const selected = selectBestExercise(contextFiltered, workout, context);
    
    if (selected) {
      workout.push(selected);
      usedExercises.add(selected.id);
    }
  }
  
  return workout;
};

/**
 * Determine the workout context based on available exercises and criteria
 */
const determineWorkoutContext = (candidates, criteria) => {
  const context = {
    location: 'mixed',
    equipment: 'mixed',
    type: 'mixed'
  };
  
  // Analyze exercise distribution
  const locations = candidates.map(ex => getExerciseLocation(ex));
  const equipments = candidates.map(ex => ex.equipment);
  const categories = candidates.map(ex => ex.category);
  
  // Determine primary location
  const outdoorCount = locations.filter(loc => loc === 'outdoor').length;
  const indoorCount = locations.filter(loc => loc === 'indoor').length;
  
  if (outdoorCount > indoorCount) {
    context.location = 'outdoor';
  } else if (indoorCount > outdoorCount) {
    context.location = 'indoor';
  }
  
  // Determine equipment type with better detection
  const bodyweightCount = equipments.filter(eq => 
    ['body only', 'none', 'resistance band'].includes(eq?.toLowerCase())
  ).length;
  
  const gymCount = equipments.filter(eq => 
    ['barbell', 'dumbbell', 'machine', 'cable', 'smith machine'].includes(eq?.toLowerCase())
  ).length;
  
  console.log('Equipment analysis:', { bodyweightCount, gymCount, total: candidates.length });
  
  if (bodyweightCount > gymCount && bodyweightCount > candidates.length * 0.3) {
    context.equipment = 'bodyweight';
    console.log('Setting context to bodyweight');
  } else if (gymCount > bodyweightCount && gymCount > candidates.length * 0.3) {
    context.equipment = 'gym';
    console.log('Setting context to gym');
  }
  
  return context;
};

/**
 * Get exercise location based on equipment and name
 */
const getExerciseLocation = (exercise) => {
  const name = exercise.name.toLowerCase();
  const equipment = exercise.equipment?.toLowerCase() || '';
  
  // Outdoor indicators - very specific outdoor activities
  if (name.includes('outdoor') || name.includes('park') || name.includes('trail') ||
      name.includes('beach') || name.includes('track') || name.includes('hiking') ||
      name.includes('outdoor running') || name.includes('outdoor cycling') ||
      name.includes('trail running') || name.includes('beach run')) {
    return 'outdoor';
  }
  
  // Gym machine indicators - very specific gym equipment
  if (equipment.includes('stairmaster') || equipment.includes('treadmill') ||
      equipment.includes('elliptical') || equipment.includes('rowing machine') ||
      name.includes('stairmaster') || name.includes('treadmill') ||
      name.includes('elliptical') || name.includes('rowing machine')) {
    return 'gym_machine';
  }
  
  // Indoor gym indicators
  if (equipment.includes('barbell') || equipment.includes('dumbbell') ||
      equipment.includes('machine') || equipment.includes('cable') ||
      equipment.includes('smith') || name.includes('gym')) {
    return 'indoor';
  }
  
  return 'mixed';
};

/**
 * Filter exercises by workout context
 */
const filterByContext = (exercises, context, currentWorkout) => {
  if (currentWorkout.length === 0) {
    return exercises; // First exercise - no constraints
  }
  
  const currentLocation = getExerciseLocation(currentWorkout[0]);
  const currentEquipment = currentWorkout[0].equipment;
  
  return exercises.filter(exercise => {
    const exerciseLocation = getExerciseLocation(exercise);
    const exerciseEquipment = exercise.equipment;
    
    // Location compatibility - focus on preventing outdoor + gym machine mixing
    if (currentWorkout.length > 0) {
      const currentLocation = getExerciseLocation(currentWorkout[0]);
      
      // Prevent outdoor + gym machine mixing (the main issue)
      if (currentLocation === 'outdoor' && exerciseLocation === 'gym_machine') {
        return false;
      }
      if (currentLocation === 'gym_machine' && exerciseLocation === 'outdoor') {
        return false;
      }
    }
    
    // Equipment compatibility
    if (currentEquipment && exerciseEquipment) {
      const currentEqType = getEquipmentType(currentEquipment);
      const exerciseEqType = getEquipmentType(exerciseEquipment);
      
      // Avoid mixing incompatible equipment types
      if (currentEqType === 'gym' && exerciseEqType === 'outdoor') {
        return false;
      }
      if (currentEqType === 'outdoor' && exerciseEqType === 'gym') {
        return false;
      }
      // Allow mixing of bodyweight and gym equipment - they're compatible
      // Only avoid mixing outdoor with indoor equipment
    }
    
    return true;
  });
};

/**
 * Get equipment type category
 */
const getEquipmentType = (equipment) => {
  if (['body only', 'none', 'resistance band'].includes(equipment)) {
    return 'bodyweight';
  }
  if (['barbell', 'machine', 'cable', 'smith machine'].includes(equipment)) {
    return 'gym';
  }
  if (['outdoor', 'park', 'trail'].includes(equipment)) {
    return 'outdoor';
  }
  return 'mixed';
};

/**
 * Select the best exercise using smart scoring
 */
const selectBestExercise = (exercises, currentWorkout, context) => {
  if (exercises.length === 0) return null;
  
  // Score each exercise
  const scoredExercises = exercises.map(exercise => ({
    ...exercise,
    score: calculateExerciseScore(exercise, currentWorkout, context)
  }));
  
  // Sort by score and select
  scoredExercises.sort((a, b) => b.score - a.score);
  
  // Add some randomness to top candidates
  const topCandidates = scoredExercises.slice(0, Math.min(3, scoredExercises.length));
  return topCandidates[Math.floor(Math.random() * topCandidates.length)];
};

/**
 * Calculate exercise score based on compatibility
 */
const calculateExerciseScore = (exercise, currentWorkout, context) => {
  let score = 50; // Base score
  
  // Location bonus
  const exerciseLocation = getExerciseLocation(exercise);
  if (context.location === exerciseLocation) {
    score += 20;
  }
  
  // Equipment compatibility bonus
  if (currentWorkout.length > 0) {
    const currentEquipment = currentWorkout[0].equipment;
    const currentEqType = getEquipmentType(currentEquipment);
    const exerciseEqType = getEquipmentType(exercise.equipment);
    
    if (currentEqType === exerciseEqType) {
      score += 15;
    } else if (currentEqType === 'bodyweight' && exerciseEqType === 'gym') {
      // Bodyweight and gym equipment are compatible
      score += 10;
    } else if (currentEqType === 'gym' && exerciseEqType === 'bodyweight') {
      // Gym and bodyweight equipment are compatible
      score += 10;
    }
  }
  
  // Variety bonus (different muscle groups)
  const usedMuscles = new Set();
  currentWorkout.forEach(ex => {
    ex.primaryMuscles?.forEach(muscle => usedMuscles.add(muscle));
  });
  
  const newMuscles = exercise.primaryMuscles?.filter(muscle => !usedMuscles.has(muscle)).length || 0;
  score += newMuscles * 5;
  
  // Category consistency bonus
  if (currentWorkout.length > 0) {
    const currentCategory = currentWorkout[0].category;
    if (exercise.category === currentCategory) {
      score += 10;
    }
  }
  
  return score;
};

/**
 * Get muscle group aliases for matching
 */
const getMuscleGroupAliases = (group) => {
  const muscleGroupMap = {
    chest: ["chest"],
    back: ["lats", "middle back", "lower back", "rhomboids", "traps"],
    shoulders: ["shoulders", "rear delts", "front delts"],
    biceps: ["biceps"],
    triceps: ["triceps"],
    legs: ["quadriceps", "hamstrings", "calves"],
    glutes: ["glutes"],
    core: ["abdominals", "abs"],
  };
  
  return muscleGroupMap[group] || [];
};
